/**
 * SceneBuilder (WR.3.3): the thin renderer that realizes a pure `ScenePlan`
 * (plan.ts) into Three.js objects on a scene, and asynchronously UPGRADES the
 * placeholder primitives to delivered pack assets via the WR.3.2 `AssetLoader`.
 *
 * It replaces Game.ts#buildWorld()/buildWorldMarkers(). Every decision (what to
 * draw, what to upgrade) lives in the pure, unit-tested planScene(); this module
 * only maps plan data to meshes and manages their lifecycle (rebuild per join,
 * dispose on replace). It is presentation-only and off the per-tick path
 * (wr3-asset-pipeline-standup.md §4): it renders meshes, reads the existing join
 * descriptor, and never feeds a render mesh into collision — the runtime collider
 * stays the WR.2 shared source (AD-026/AD-047). Like Game.ts/threeLoader.ts it is
 * browser glue exercised by the build, not by `node --test` (§12 risk 1: the
 * testable logic is planScene; the loader is injected, so no GPU path is wired in
 * here).
 *
 * Import boundary (AD-122): shared content types via `@great-cloud/shared/client`;
 * `three` is allowed in frontend/.
 */

import * as THREE from 'three';

import type {
  AssetPackDef,
  ClientPackDef,
  ThemeDef,
  WorldDescriptor,
} from '@great-cloud/shared/client';

import {
  AssetLoader,
  AssetManifest,
  disposeObject3D,
  LOD_CULLED,
  PackLoaderCache,
  selectLod,
  type GltfLoadFn,
} from '../assets/index.js';
import { fullDetailAssetsActive, packBaseAssetsActive } from './assetDetail.js';
import { UpgradeQueue, type AssetUpgradeGate } from './assetUpgradeGate.js';
import { budgetWarnings, describeBudgetWarning, type RenderTier } from './budget.js';
import {
  groundAlbedoFor,
  loadGroundAlbedo,
  type GroundAlbedoHandle,
  type GroundTextureLoad,
} from './groundAlbedo.js';
import { chunkVisible, coverInputFromPlan, planGroundCover } from './groundCover.js';
import {
  buildGroundCover,
  createGroundCoverMaterial,
  type GroundCoverHandle,
  type GroundCoverMaterialFactory,
} from './groundCoverMesh.js';
import { createGroundMaterial, type GroundMaterialFactory } from './groundMaterial.js';
import { resolveGroundPalette } from './groundPalette.js';
import { createSky, type SkyFactory } from './sky.js';
import { MERGE_SLICE_MS, StaticMergeQueue, representedMeshCount } from './staticMerge.js';
import { activeQualityProfile, type ShadowParams } from './quality.js';
import {
  SCATTER_INSTANCES_NAME,
  instanceTemplate,
  type InstancePlacement,
} from './scatterInstancing.js';
import {
  EMPTY_DESCRIPTOR,
  planScene,
  propGroundY,
  seatTexturedPlacement,
  type AssetPlacementPlan,
  type BoxDims,
  type ColliderPlan,
  type DressingOptions,
  type DressingPlan,
  type GroundPlan,
  type LightPlan,
  type MarkerPlan,
  type PropPlan,
  type ScenePlan,
} from './plan.js';
import {
  drapedGridArrays,
  heightfieldArrays,
  heightfieldClippedArrays,
  heightfieldHeightAt,
} from './terrain.js';
import { markWorldContent } from './worldContent.js';
import { buildIsland, type IslandHandle } from './islandMesh.js';
import { islandFramingExtent, lipRim } from './island.js';
import {
  GLOBAL_ANCHOR,
  pointsAnchor,
  type PendingAnchor,
  type PendingAssetSink,
} from '../readiness.js';

/**
 * The legacy grid was tuned to read against a flat dark-blue ground; over the
 * procedural ground (2026-09-14) it is softened so the ground reads as ground,
 * while still giving a 2 m scale reference. Applied to both the flat
 * GridHelper and the draped heightfield grid; the plan's opacity is unchanged.
 */
const GROUND_GRID_SOFTEN = 0.4;

/** Measure a template/object's natural world-space AABB as plain numbers (for seatTexturedPlacement). */
function boxDimsOf(object: THREE.Object3D): BoxDims {
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  return {
    minX: box.min.x,
    minY: box.min.y,
    minZ: box.min.z,
    maxX: box.max.x,
    maxY: box.max.y,
    maxZ: box.max.z,
  };
}

/** A slot a template is placed at (the fields scatter + dressing plans share). */
interface TemplateSlot {
  x: number;
  y: number;
  z: number;
  scale: number;
  rotationY: number;
}

/** The slot's final transform: seated against the proxy when both boxes are known. */
function slotTransform(
  texBox: BoxDims | null,
  refBox: BoxDims | null,
  slot: TemplateSlot,
): InstancePlacement {
  if (texBox && refBox) {
    const seat = seatTexturedPlacement(texBox, refBox, slot);
    return { ...seat, rotationY: slot.rotationY };
  }
  const { x, y, z, scale, rotationY } = slot;
  return { x, y, z, rotationY, scaleX: scale, scaleY: scale, scaleZ: scale };
}

/** One clone of `template` per slot (settlement pieces, and refused scatter templates). */
function placeClones(
  template: THREE.Object3D,
  texBox: BoxDims | null,
  refBox: BoxDims | null,
  slots: readonly TemplateSlot[],
): THREE.Group {
  const placed = new THREE.Group();
  for (const slot of slots) {
    const t = slotTransform(texBox, refBox, slot);
    const object = template.clone(true);
    object.scale.set(t.scaleX, t.scaleY, t.scaleZ);
    object.position.set(t.x, t.y, t.z);
    object.rotation.y = t.rotationY;
    placed.add(object);
  }
  return placed;
}

/**
 * E3.3.S3 (AD-158 §2): the ONE scatter placement path — instanced, falling back
 * to clones only for a template `instanceRefusal` rejects. The returned group is
 * named `SCATTER_INSTANCES_NAME` when instanced (the caller skips the merge).
 */
function placeScatterTemplate(
  template: THREE.Object3D,
  texBox: BoxDims | null,
  refBox: BoxDims | null,
  slots: readonly TemplateSlot[],
): THREE.Group {
  const instanced = instanceTemplate(
    template,
    slots.map((slot) => slotTransform(texBox, refBox, slot)),
  );
  return instanced ?? placeClones(template, texBox, refBox, slots);
}

/** How Game resolves a descriptor's theme/assetPack ids to content defs (from the content pack). */
export interface SceneContentLookup {
  theme(id: string): ThemeDef | undefined;
  assetPack(id: string): AssetPackDef | undefined;
  /**
   * The joined zone's biome def, as PROJECTED into the client pack (AD-315):
   * the source of the `generation` dressing rules. Untyped on purpose — the
   * pack is plain JSON and `clientGenerationOf` narrows it defensively.
   */
  biome?(id: string): ClientPackDef | undefined;
  /**
   * A `terrain-tiles` set as PROJECTED into the client pack (AD-408: grid params +
   * elevation-only tiles), narrowed defensively by `clientTerrainSetOf`. Absent ⇒
   * every zone renders the flat AD-315 ground.
   */
  terrainSet?(id: string): ClientPackDef | undefined;
}

export interface SceneBuilderOptions {
  /** Real glTF loader (browser: `createThreeGltfLoader()`); absent ⇒ placeholder-only (no upgrades). */
  gltfLoad?: GltfLoadFn;
  /** Static asset HTTP base for asset delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /** Render the collision footprints as a dev debug overlay (default false). */
  colliderOverlay?: boolean;
  /** Budget tier for the dev draw-call/triangle assert (WR.3.4; default 'desktop'). */
  tier?: RenderTier;
  /** Tier-controlled shadow config for the sun (VQ-001/AD-194; default disabled). */
  shadows?: ShadowParams;
  /** Non-fatal warning sink (default console.warn). */
  onWarn?: (message: string, error?: unknown) => void;
  /**
   * Resolve an asset-pack id to its client-pack def (AD-315). Biome dressing is
   * MULTI-pack — a settlement kit and a scatter kit are different packs, neither
   * necessarily the descriptor's bound pack — so it cannot ride the single
   * `AssetManifest`. Absent ⇒ dressing is planned but not drawn.
   */
  resolvePack?: (packId: string) => AssetPackDef | undefined;
  /**
   * Ground texture loader (browser: `createBrowserGroundTextureLoad()`). Absent ⇒ the procedural
   * ground only — node tests construct the builder without one and need no DOM.
   */
  loadGroundTexture?: GroundTextureLoad;
  /**
   * The ground material constructor for this renderer (AD-651 amendment): absent ⇒ the
   * WebGL2 GLSL default; Game passes the WebGPU node-material sibling on the webgpu path.
   */
  groundMaterial?: GroundMaterialFactory;
  /**
   * The sky-dome constructor for this renderer (AD-651 item 2): absent ⇒ the WebGL2
   * `Sky.js` default; Game passes the TSL `SkyMesh` sibling on the webgpu path.
   */
  sky?: SkyFactory;
  /** Hold the sky's cloud drift at t = 0 (the `skystill` dev flag; lane / captures). */
  skyStill?: boolean;
  /**
   * E3.3.S5 ground-cover material (AD-159): absent ⇒ the WebGL2 GLSL default; Game passes the
   * TSL sibling on the webgpu path (groundCoverNodeMaterial.ts); `null` ⇒ no ground cover.
   */
  groundCover?: GroundCoverMaterialFactory | null;
  /**
   * Where in-flight loads are reported (q-asset-readiness-loading-screen): every prop,
   * dressing and marker upgrade plus the ground texture, each anchored where it will appear,
   * so the loading screen can hold until what is in view has arrived. Absent ⇒ unreported.
   */
  pending?: PendingAssetSink;
  /**
   * q-desktop-proxy-first-asset-upgrade: while this gate is closed, full detail draws the
   * proxies (LOD0) and queues the textured bases; at release they swap in, nearest the
   * camera first. Absent (or released) ⇒ textured bases load at once, as before.
   */
  upgradeGate?: Pick<AssetUpgradeGate, 'released' | 'onRelease'>;
  /** The queue those bases wait in (shared with the NPC stations). Absent ⇒ the builder's own. */
  upgradeQueue?: UpgradeQueue;
}

/** A distance-cullable world group: the object to toggle + its world (x,z) centroid. */
interface Cullable {
  object: THREE.Object3D;
  x: number;
  z: number;
}

export class SceneBuilder {
  private readonly scene: THREE.Scene;
  private readonly gltfLoad?: GltfLoadFn;
  private readonly httpBase: string;
  private readonly colliderOverlay: boolean;
  private readonly tier: RenderTier;
  private readonly shadows: ShadowParams;
  private readonly onWarn: (message: string, error?: unknown) => void;
  private readonly resolvePack?: (packId: string) => AssetPackDef | undefined;
  private readonly pending: PendingAssetSink | null;
  private readonly upgradeGate: Pick<AssetUpgradeGate, 'released' | 'onRelease'> | null;
  /** Textured bases waiting for the upgrade gate (proxy-first desktop). */
  private readonly upgrades: UpgradeQueue;
  /** The camera's ground position at the last `updateView` (where the upgrade starts). */
  private lastEye = { x: 0, z: 0 };

  /** Scene-level lights (removed + rebuilt on each apply). */
  private lights: THREE.Object3D[] = [];
  /** Everything rebuilt per join (ground, grid, props, markers, overlay). */
  private worldRoot: THREE.Group | null = null;
  /** The loader for the current pack (cleared on the next apply). */
  private loader: AssetLoader | null = null;
  /** Bumped on every apply so a stale async upgrade for a previous world is dropped. */
  private generation = 0;
  /** Distance-cullable scatter groups (markers stay always-visible — they are landmarks). */
  private cullables: Cullable[] = [];
  /** One AssetLoader per dressing pack, reused across joins (AD-315). */
  private dressingLoaders: PackLoaderCache | null = null;
  /**
   * The WR.2 landmark markers (shrine cone / transition torus / poi post) of the
   * current world, grouped so capture footage can hide them as one. They are
   * gameplay wayfinding aids, not scenery: the cyan shrine cone stood in every
   * shot of the 2026-W37 devlog video (owner report, 2026-09-09).
   */
  private markersGroup: THREE.Group | null = null;
  private markersVisible = true;
  /** AD-408: the current heightfield ground mesh (null on flat ground) — click-to-move raycasts it. */
  private terrainMesh: THREE.Mesh | null = null;
  private readonly loadGroundTexture?: GroundTextureLoad;
  private readonly groundMaterialFactory: GroundMaterialFactory;
  private readonly skyFactory: SkyFactory;
  private readonly skyStill: boolean;
  private readonly groundCoverFactory: GroundCoverMaterialFactory | null;
  /** E3.3.S5: the current zone's ground cover (null when its palette grows none). */
  private groundCover: GroundCoverHandle | null = null;
  /** AD-840: the current zone's floating island (null for a rectangle zone). */
  private island: IslandHandle | null = null;
  /**
   * AD-840: the square of cloud sea drawn under an island zone (null for a rectangle zone, or an
   * island with no sea). Past the bounds rectangle the camera sees this sea, not void, so capture
   * framing measures void against it — the W44 render (2026-10-28) refused the echo yard because
   * the rectangle still counted the intended coastline view as the map edge.
   */
  private seaExtent: { minX: number; minZ: number; maxX: number; maxZ: number } | null = null;
  /** The current sky dome (null when the plan has no sky) and the plan values it was built from. */
  private skyMesh: THREE.Object3D | null = null;
  private skyKey = '';
  /**
   * Ground textures by palette id, each loaded on first use and shared across meshes and joins, so
   * returning to a zone never downloads its ground again.
   */
  private readonly groundAlbedos = new Map<string, GroundAlbedoHandle>();
  /**
   * E3.3.S2 (AD-158 §1/§5): placed dressing groups are drawn at once, then their static
   * leaves are merged per (cell × material) a slice per frame from `updateView`.
   */
  private readonly mergeQueue = new StaticMergeQueue();

  constructor(scene: THREE.Scene, options: SceneBuilderOptions = {}) {
    this.scene = scene;
    this.gltfLoad = options.gltfLoad;
    this.httpBase = options.httpBase ?? '';
    this.colliderOverlay = options.colliderOverlay ?? false;
    this.tier = options.tier ?? 'desktop';
    this.shadows = options.shadows ?? { enabled: false, mapSize: 0 };
    this.onWarn = options.onWarn ?? ((m, e) => console.warn(`[scene] ${m}`, e ?? ''));
    this.resolvePack = options.resolvePack;
    this.pending = options.pending ?? null;
    this.upgradeGate = options.upgradeGate ?? null;
    this.upgrades = options.upgradeQueue ?? new UpgradeQueue();
    this.upgradeGate?.onRelease(() => this.upgrades.start(this.lastEye));
    this.loadGroundTexture = options.loadGroundTexture;
    this.groundMaterialFactory = options.groundMaterial ?? createGroundMaterial;
    this.skyFactory = options.sky ?? createSky;
    this.skyStill = options.skyStill ?? false;
    this.groundCoverFactory =
      options.groundCover === undefined ? createGroundCoverMaterial : options.groundCover;
  }

  /**
   * The ground texture handle for a plan's palette (null palette ⇒ the default palette), or null when
   * no loader was injected (procedural only).
   */
  private groundAlbedoHandle(palette: string | null | undefined): GroundAlbedoHandle | null {
    if (!this.loadGroundTexture) return null;
    const paletteId = resolveGroundPalette(palette ?? null).id;
    let handle = this.groundAlbedos.get(paletteId);
    if (!handle) {
      handle = loadGroundAlbedo(
        groundAlbedoFor(paletteId),
        this.httpBase,
        this.trackedTextureLoad(this.loadGroundTexture, `ground texture: ${paletteId}`),
        (m) => this.onWarn(m),
      );
      this.groundAlbedos.set(paletteId, handle);
    }
    return handle;
  }

  /** Report a load to the readiness registry (a pass-through when none was injected). */
  private track<T>(label: string, anchor: PendingAnchor, work: Promise<T>): Promise<T> {
    return this.pending ? this.pending.track(label, anchor, work) : work;
  }

  /** Wrap a callback-style texture load so it is reported until it loads or fails. */
  private trackedTextureLoad(load: GroundTextureLoad, label: string): GroundTextureLoad {
    const pending = this.pending;
    if (pending === null) return load;
    return (url, onLoad, onError) => {
      const done = pending.begin(label, GLOBAL_ANCHOR); // the ground is everywhere in view
      return load(
        url,
        () => {
          done();
          onLoad();
        },
        () => {
          done();
          onError();
        },
      );
    };
  }

  /** The pre-join placeholder world (the exact legacy buildWorld() output). */
  buildPlaceholder(): void {
    this.applyWorld(EMPTY_DESCRIPTOR, new AssetManifest(null));
  }

  /**
   * Show or hide the landmark markers (persists across re-applies). Capture mode
   * hides them by default (`captureMarkersHidden()`); normal play always shows them.
   */
  setMarkersVisible(visible: boolean): void {
    this.markersVisible = visible;
    if (this.markersGroup) this.markersGroup.visible = visible;
  }

  /** The current world's heightfield ground mesh, or null when the ground is flat (AD-408). */
  terrainObject(): THREE.Object3D | null {
    return this.terrainMesh;
  }

  /** Whether the landmark markers are currently shown. */
  markersShown(): boolean {
    return this.markersVisible;
  }

  /**
   * Number of renderable meshes under the current world root — the capture
   * readiness signal `worldMeshes` (CloudMediaStudio `clip_capture.py`). The
   * placeholder world is a handful (ground, grid, markers); a dressed zone is
   * dozens, so a floor on this count separates "the zone's assets have arrived"
   * from "the world descriptor arrived" — 2026-W37 segment 2 opened on the former.
   */
  meshCount(): number {
    // E3.3.S2: a merged mesh counts as the meshes it replaced, so the studio's floor
    // keeps meaning "the kit has arrived" whether or not the merge has run yet.
    return this.worldRoot ? representedMeshCount(this.worldRoot) : 0;
  }

  /** E3.3.S2: static-merge progress (buckets waiting · committed · slowest commit ms). */
  mergeStats(): { pending: number; merged: number; maxCommitMs: number } {
    return {
      pending: this.mergeQueue.pending(),
      merged: this.mergeQueue.merged,
      maxCommitMs: this.mergeQueue.maxCommitMs,
    };
  }

  /**
   * Rebuild the world from a join descriptor + resolved manifest/theme. The plan
   * decides everything (placeholder vs upgrade); this realizes it and kicks the
   * async asset upgrades. Re-callable (e.g. once the content pack finishes loading).
   */
  applyWorld(
    descriptor: WorldDescriptor,
    manifest: AssetManifest,
    theme?: ThemeDef | null,
    dressing?: DressingOptions | null,
  ): void {
    const plan = planScene(descriptor, manifest, theme ?? null, {
      colliderOverlay: this.colliderOverlay,
      dressing: dressing ?? null,
    });
    const loader =
      this.gltfLoad && manifest.bound
        ? new AssetLoader(manifest, {
            load: this.gltfLoad,
            httpBase: this.httpBase,
            onWarn: this.onWarn,
          })
        : null;
    this.realize(plan, loader);
  }

  private realize(plan: ScenePlan, loader: AssetLoader | null): void {
    const gen = ++this.generation;
    this.upgrades.clear(); // the previous world's waiting upgrades are moot

    // Scene-level: sky dome (or the flat background) + fog. With a dome the clear
    // colour is never seen, so the background is dropped; the fog colour is the
    // dome's own horizon (plan.ts), so distant geometry fades into the sky.
    // Rejoins with the same sky keep the dome (no rebuild, no shader recompile).
    const skyKey = plan.sky ? JSON.stringify(plan.sky) : '';
    if (skyKey !== this.skyKey) this.removeSky();
    if (plan.sky) {
      if (!this.skyMesh) {
        // Mobile tier / ?quality=low ⇒ a cheaper cloud loop, exactly as the ground goes lite.
        const lite = this.tier === 'mobile' || activeQualityProfile().low;
        this.skyMesh = this.skyFactory(plan.sky, { lite, still: this.skyStill });
        this.skyKey = skyKey;
        this.scene.add(markWorldContent(this.skyMesh)); // AD-705: hidden while a run is drawn
      }
      this.scene.background = null;
    } else {
      this.scene.background = new THREE.Color(plan.background);
    }
    this.scene.fog = new THREE.Fog(plan.fog.color, plan.fog.near, plan.fog.far);

    // Lights — remove the previous set, add the new (the sun carries the
    // tier-controlled shadow config, VQ-001/AD-194).
    for (const light of this.lights) dropLight(this.scene, light);
    this.lights = plan.lights.map((spec) => makeLight(spec, this.shadows));
    for (const light of this.lights) this.scene.add(light);

    // World root — dispose + replace (so re-joins/theme changes rebuild cleanly).
    if (this.worldRoot) {
      this.scene.remove(this.worldRoot);
      disposeObject(this.worldRoot);
    }
    if (this.loader) this.loader.clear();
    this.loader = loader;

    this.mergeQueue.clear();
    this.cullables = [];
    this.terrainMesh = null;
    this.groundCover = null;
    this.island = null;
    this.seaExtent = null;
    const root = new THREE.Group();
    if (plan.island) this.buildIslandGround(root, plan);
    else this.buildGround(root, plan.ground);
    this.buildScatter(root, plan.props, loader, gen);
    this.buildAssetPlacements(root, plan.assetPlacements, loader, gen);
    this.buildDressing(root, plan.dressing, gen);
    this.buildMarkers(root, plan.markers, loader, gen);
    this.buildColliderOverlay(root, plan.colliderOverlay);
    this.buildGroundCoverLayer(root, plan);
    this.scene.add(markWorldContent(root)); // AD-705: hidden while a run is drawn
    this.worldRoot = root;

    // Dev budget assert (WR.3.4): warn on draw-call/triangle overage for this tier.
    for (const warning of budgetWarnings(plan, this.tier)) {
      this.onWarn(describeBudgetWarning(warning));
    }
  }

  /**
   * The procedural ground material for a plan (2026-09-14): the plan's optional
   * biome palette (default when absent), `lite` detail on the mobile tier or an
   * explicit low-quality profile. The plan's `color`/`roughness` stay in the
   * plan (pinned by the characterization tests) but are no longer drawn.
   */
  private groundMaterial(ground: GroundPlan): THREE.Material {
    const lite = this.tier === 'mobile' || activeQualityProfile().low;
    return this.groundMaterialFactory(ground.palette ?? null, {
      detail: lite ? 'lite' : 'full',
      polygonOffset: true,
      albedo: this.groundAlbedoHandle(ground.palette),
    });
  }

  /**
   * AD-840: the zone as a floating island — its top (in the zone's own ground
   * material, so palette and detail carry over), underside, rim crags, cloud sea
   * and distant islands — in place of the bounds rectangle. Low profile ⇒ lite.
   * On a relief zone (q-island-edges-heightfield) the top is the AD-408
   * heightfield cut to the lip rim, the rest hangs off that surface, and the top
   * is the click-to-move raycast target exactly as the uncut heightfield was.
   */
  private buildIslandGround(root: THREE.Group, plan: ScenePlan): void {
    if (!plan.island) return;
    const lite = this.tier === 'mobile' || activeQualityProfile().low;
    const hf = plan.ground.heightfield;
    const { cx, cz } = plan.island.outline;
    const handle = buildIsland(
      plan.island,
      hf ? (x, z) => heightfieldHeightAt(hf, x, z) : () => 0,
      { lite },
      this.groundMaterial(plan.ground),
      plan.fog.color,
      hf ? heightfieldClippedArrays(hf, lipRim(plan.island), { x: cx, z: cz }) : undefined,
    );
    root.add(handle.group);
    this.island = handle;
    if (hf) this.terrainMesh = handle.top;
    this.seaExtent = islandFramingExtent(plan.island);
  }

  /** AD-840: the visible ground square on an island zone (its cloud sea), else null. */
  visibleGroundExtent(): { minX: number; minZ: number; maxX: number; maxZ: number } | null {
    return this.seaExtent;
  }

  private buildGround(root: THREE.Group, ground: GroundPlan): void {
    if (ground.heightfield) {
      // AD-408: the bound terrain set's heightfield — the SAME fixed-diagonal
      // surface every drawn object is seated on (terrain.ts), shaded by computed
      // vertex normals under the lights of record. Colour/roughness/polygonOffset
      // are the rectangle's; the legacy grid is DRAPED on it (a flat GridHelper
      // would slice the relief) — same size, divisions, colours and opacity.
      const arrays = heightfieldArrays(ground.heightfield);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(arrays.positions, 3));
      geometry.setIndex(new THREE.BufferAttribute(arrays.indices, 1));
      geometry.computeVertexNormals();
      geometry.computeBoundingSphere();
      const mesh = new THREE.Mesh(
        geometry,
        // 2026-09-14: the procedural ground field (groundMaterial.ts) replaces the
        // flat `ground.color` — same polygonOffset, same lights/shadows/fog.
        this.groundMaterial(ground),
      );
      mesh.name = 'terrain-heightfield';
      mesh.receiveShadow = true;
      root.add(mesh);
      this.terrainMesh = mesh;
      if (ground.grid) root.add(makeDrapedGrid(ground.heightfield, ground));
      return;
    }
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(ground.sizeX, ground.sizeZ),
      // polygonOffset pushes the ground plane very slightly back in the depth
      // buffer so that asset bases seated at exactly y=0 (the showcase places
      // every building with its base on the ground; plan.ts) reliably win the
      // depth test instead of z-fighting the coplanar ground — the "bases
      // flicker in the ground" artifact seen in the 2026-W28 capture. Purely a
      // depth-bias; no visible effect on the ground itself.
      // 2026-09-14: procedural ground field (groundMaterial.ts), polygonOffset kept.
      this.groundMaterial(ground),
    );
    plane.rotation.x = -Math.PI / 2;
    // AD-315: the plane follows the ZONE'S bounds, so an off-origin zone
    // (ashenveil-depths spans x in [80, 240]) stands on ground instead of over
    // a void. The legacy placeholder plans x = z = 0, so this is a no-op there.
    plane.position.set(ground.x, 0, ground.z);
    plane.receiveShadow = true; // VQ-001: the ground catches the sun's shadows
    root.add(plane);
    if (ground.grid) {
      const grid = new THREE.GridHelper(
        ground.grid.size,
        ground.grid.divisions,
        ground.grid.colorCenter,
        ground.grid.colorGrid,
      );
      const material = grid.material as THREE.Material;
      material.transparent = true;
      material.opacity = ground.grid.opacity * GROUND_GRID_SOFTEN;
      grid.position.set(ground.x, ground.grid.y, ground.z);
      root.add(grid);
    }
  }

  private buildAssetPlacements(
    root: THREE.Group,
    placements: AssetPlacementPlan[],
    loader: AssetLoader | null,
    gen: number,
  ): void {
    if (!loader) return;
    const textured = fullDetailAssetsActive();
    const hold = this.holdingUpgrades();
    for (const placement of placements) {
      if (textured && hold) {
        // Proxy now; the textured base replaces it once the loading screen has lifted.
        this.placeProxyAsset(root, placement, loader, gen, (proxy) =>
          this.upgrades.add(placement.x, placement.z, () =>
            this.placeTexturedAsset(root, placement, loader, gen, proxy),
          ),
        );
      } else if (textured) void this.placeTexturedAsset(root, placement, loader, gen);
      else this.placeProxyAsset(root, placement, loader, gen);
    }
  }

  /** Whether full detail draws proxies first and queues its textured bases (gate closed). */
  private holdingUpgrades(): boolean {
    return this.upgradeGate !== null && !this.upgradeGate.released;
  }

  /** Upgrade-queue state for the scene dump: started, waiting, running, finished. */
  upgradeState(): UpgradeQueue['state'] {
    return this.upgrades.state;
  }

  /** Degraded path: instantiate the near proxy LOD and place it exactly per the plan. */
  private placeProxyAsset(
    root: THREE.Group,
    placement: AssetPlacementPlan,
    loader: AssetLoader,
    gen: number,
    onPlaced?: (proxy: THREE.Object3D) => void,
  ): void {
    void this.track(
      `prop: ${placement.assetId}`,
      pointsAnchor([placement]),
      loader.instantiate(placement.assetId),
    )
      .then((object) => {
        if (object === null || gen !== this.generation || this.worldRoot !== root) return;
        object.position.set(placement.x, placement.y, placement.z);
        object.scale.setScalar(placement.scale);
        object.rotation.y = placement.rotationY;
        root.add(object);
        onPlaced?.(object);
      })
      .catch((error: unknown) =>
        this.onWarn(`asset placement '${placement.assetId}' failed`, error),
      );
  }

  /**
   * Full-detail path: load the TEXTURED render (the entry's base `url`) and
   * seat it to occupy the SAME world footprint as the correctly-placed proxy, so
   * devlog footage shows the painted art (nimbus is re-textured). Measures the
   * textured mesh and the world-scale proxy, then applies `seatTexturedPlacement`.
   * If no textured render is available (unbound / load failure) it falls back to
   * the proxy so the capture never drops to a bare primitive. Generation-guarded.
   */
  private placeTexturedAsset(
    root: THREE.Group,
    placement: AssetPlacementPlan,
    loader: AssetLoader,
    gen: number,
    replaces?: THREE.Object3D,
  ): Promise<void> {
    const load = Promise.all([
      loader.instantiateBase(placement.assetId),
      loader.template(placement.assetId, 0),
    ]);
    return (
      replaces ? load : this.track(`prop: ${placement.assetId}`, pointsAnchor([placement]), load)
    )
      .then(([textured, proxyTpl]) => {
        if (gen !== this.generation || this.worldRoot !== root) return;
        if (textured === null) {
          if (!replaces) this.placeProxyAsset(root, placement, loader, gen); // else keep the proxy
          return;
        }
        const texBox = boxDimsOf(textured);
        const refBox = proxyTpl ? boxDimsOf(proxyTpl) : texBox;
        const seat = seatTexturedPlacement(texBox, refBox, {
          x: placement.x,
          y: placement.y,
          z: placement.z,
          scale: placement.scale,
        });
        textured.scale.set(seat.scaleX, seat.scaleY, seat.scaleZ);
        textured.position.set(seat.x, seat.y, seat.z);
        textured.rotation.y = placement.rotationY;
        root.add(textured);
        // The proxy clone shares its template's geometry with every other clone: detach only.
        if (replaces) root.remove(replaces);
      })
      .catch((error: unknown) =>
        this.onWarn(`textured asset placement '${placement.assetId}' failed`, error),
      );
  }

  // Dense scatter → one `InstancedMesh` per upgrade group (the WR.3.4 perf
  // increment: N rocks become ONE draw call). Grouping by `upgradeAssetId` keeps
  // the safe per-group upgrade (planScene assigns all rocks the same id today, so
  // it is one group) and lets the whole group distance-cull as a unit.
  private buildScatter(
    root: THREE.Group,
    props: PropPlan[],
    loader: AssetLoader | null,
    gen: number,
  ): void {
    if (props.length === 0) return;
    const groups = new Map<string | null, PropPlan[]>();
    for (const prop of props) {
      const list = groups.get(prop.upgradeAssetId);
      if (list) list.push(prop);
      else groups.set(prop.upgradeAssetId, [prop]);
    }
    for (const [assetId, group] of groups) {
      const mesh = makeRockInstances(group);
      root.add(mesh);
      this.trackCullable(mesh, group);
      // Upgrade the whole group: load the near LOD once, place a clone per slot.
      if (assetId && loader) void this.upgradeScatter(root, mesh, assetId, group, loader, gen);
    }
  }

  /** Track a scatter group's centroid so `updateView` can distance-cull it as a unit. */
  private trackCullable(object: THREE.Object3D, group: readonly { x: number; z: number }[]): void {
    let x = 0;
    let z = 0;
    for (const p of group) {
      x += p.x;
      z += p.z;
    }
    const n = group.length || 1;
    this.cullables.push({ object, x: x / n, z: z / n });
  }

  /**
   * Draw the biome dressing (AD-315): settlement kit pieces at the SHARED
   * placement law's transforms + the deterministic scatter.
   *
   * Grouped by (pack, asset) so each distinct asset is loaded and templated
   * ONCE and cloned per placement — the `upgradeScatter` pattern generalised
   * across packs — and so each group distance-culls as a unit. Every stage
   * degrades rather than throws: no `resolvePack`/`gltfLoad`, an unknown pack
   * id, or an asset the pack does not deliver each drop that group with a warn,
   * leaving the rest of the world intact (WR.3.3 graceful degradation).
   */
  private buildDressing(root: THREE.Group, dressing: DressingPlan[], gen: number): void {
    if (dressing.length === 0) return;

    // E3.4.S2 — the fallback runs BEFORE the loader guard, on purpose: a client
    // with no glTF loader at all is exactly the case where a colliding piece
    // would otherwise be an invisible wall. Pieces the plan already marked
    // undeliverable never attempt a load.
    const resolvePack = this.resolvePack;
    const loadable = this.gltfLoad !== undefined && resolvePack !== undefined;
    this.placeDressingPlaceholders(
      root,
      dressing.filter((d) => d.placeholder || !loadable),
    );
    if (!this.gltfLoad || !resolvePack) return;
    this.dressingLoaders ??= new PackLoaderCache({
      gltfLoad: this.gltfLoad,
      httpBase: this.httpBase,
      onWarn: this.onWarn,
    });
    const cache = this.dressingLoaders;

    const groups = new Map<string, DressingPlan[]>();
    for (const placement of dressing) {
      if (placement.placeholder) continue;
      const key = `${placement.assetPack}::${placement.assetId}`;
      const list = groups.get(key);
      if (list) list.push(placement);
      else groups.set(key, [placement]);
    }

    for (const group of groups.values()) {
      const first = group[0];
      if (first === undefined) continue;
      const packDef = resolvePack(first.assetPack);
      if (!packDef) {
        this.onWarn(`dressing pack '${first.assetPack}' is not in the content pack`);
        this.placeDressingPlaceholders(root, group);
        continue;
      }
      // q-flora-low-tier-textures: the pack's `lowQualityBase` decides the low-profile band.
      void this.placeDressingGroup(
        root,
        cache.loaderFor(packDef),
        first.assetId,
        group,
        gen,
        packBaseAssetsActive(packDef),
      );
    }
  }

  /**
   * E3.4.S2 graceful fallback: draw a plain box at each placement whose asset the
   * client cannot deliver, so a piece the SERVER collides against is never
   * invisible. One InstancedMesh for the whole set (one draw call), tracked as a
   * single cullable like any other dressing group.
   *
   * Deliberately NOT the scatter dodecahedron: this is scaffolding a player is
   * meant to read as "a building should be here", not as a rock.
   */
  private placeDressingPlaceholders(root: THREE.Group, group: readonly DressingPlan[]): void {
    // SETTLEMENT PIECES ONLY. Scatter is visual-only and non-colliding, so a
    // missing scatter asset is correctly nothing at all — scaffolding it would
    // put grey boxes where the world is meant to be empty. Filtered HERE rather
    // than at each call site so every drop path obeys the same rule.
    const pieces = group.filter((d) => d.settlementId !== null);
    if (pieces.length === 0) return;
    const mesh = makeDressingPlaceholders(pieces);
    root.add(mesh);
    this.trackCullable(mesh, pieces);
    this.onWarn(`dressing: ${pieces.length} piece(s) drawn as placeholders (asset not delivered)`);
  }

  /**
   * Template one dressing asset, clone it to each placement, track the group as one
   * cullable. `fullDetail` = draw the textured base (`packBaseAssetsActive`), else LOD0.
   */
  private placeDressingGroup(
    root: THREE.Group,
    loader: AssetLoader,
    assetId: string,
    group: DressingPlan[],
    gen: number,
    fullDetail: boolean,
    replaces?: THREE.Object3D,
  ): Promise<void> {
    // Proxy-first: full detail draws the proxy group now and queues the textured one.
    const hold = fullDetail && replaces === undefined && this.holdingUpgrades();
    const load = Promise.all([
      fullDetail && !hold ? loader.templateBase(assetId) : Promise.resolve(null),
      loader.template(assetId, 0),
    ]);
    return (replaces ? load : this.track(`dressing: ${assetId}`, pointsAnchor(group), load))
      .then(([baseTemplate, proxyTemplate]) => {
        if (gen !== this.generation || this.worldRoot !== root) return;
        if (replaces !== undefined && baseTemplate === null) {
          // No textured base after all: the proxy group stays, and may merge now.
          this.enqueueMerge(replaces, root, gen);
          return;
        }
        const template = baseTemplate ?? proxyTemplate;
        if (template === null) {
          // E3.4.S2: the pack claimed the asset but it did not load. Same cure —
          // scaffolding beats an invisible wall.
          this.placeDressingPlaceholders(root, group);
          return;
        }
        const texBox = baseTemplate ? boxDimsOf(baseTemplate) : null;
        const refBox = baseTemplate ? (proxyTemplate ? boxDimsOf(proxyTemplate) : texBox) : null;
        // E3.3.S3: scatter (no settlement) is AP6-density ⇒ instanced (AD-158 §2).
        // Settlement kit pieces stay clones so the static merge can fold several
        // DIFFERENT assets of one neighbourhood into one cell mesh (E3.3.S2).
        const scatter = group.every((placement) => placement.settlementId === null);
        const placed = scatter
          ? placeScatterTemplate(template, texBox, refBox, group)
          : placeClones(template, texBox, refBox, group);
        root.add(placed);
        if (replaces !== undefined) {
          this.retargetCullable(replaces, placed);
          root.remove(replaces); // shares the cached proxy template's geometry: detach only
        } else this.trackCullable(placed, group);
        // A held proxy group stays unmerged, so the textured group can replace it whole.
        if (hold) {
          const at = centroidOf(group);
          this.upgrades.add(at.x, at.z, () =>
            this.placeDressingGroup(root, loader, assetId, group, gen, true, placed),
          );
        } else this.enqueueMerge(placed, root, gen);
      })
      .catch((error: unknown) => {
        this.onWarn(`dressing asset '${assetId}' failed`, error);
        if (replaces === undefined && gen === this.generation && this.worldRoot === root)
          this.placeDressingPlaceholders(root, group);
      });
  }

  /** E3.3.S2: drawn now, merged over the next frames (behavioural nodes stay live). */
  private enqueueMerge(placed: THREE.Object3D, root: THREE.Group, gen: number): void {
    if (placed.name === SCATTER_INSTANCES_NAME) return;
    this.mergeQueue.enqueue(placed, () => gen === this.generation && this.worldRoot === root);
  }

  /**
   * Upgrade an instanced placeholder scatter group to a delivered pack asset:
   * load the near LOD (`lod 0`, via the loader's `AssetEntry.lods[]` resolution)
   * ONCE, clone it to each slot, swap it in for the placeholder InstancedMesh (and
   * retarget the cullable). Generation-guarded; a null result keeps the placeholder.
   */
  private upgradeScatter(
    root: THREE.Group,
    placeholder: THREE.Object3D,
    assetId: string,
    group: PropPlan[],
    loader: AssetLoader,
    gen: number,
    textured = false,
  ): Promise<void> {
    const fullDetail = fullDetailAssetsActive();
    // Proxy-first: the placeholder rocks become proxies now, the textured base later.
    const hold = !textured && fullDetail && this.holdingUpgrades();
    const load = Promise.all([
      fullDetail && !hold ? loader.templateBase(assetId) : Promise.resolve(null),
      loader.template(assetId, 0),
    ]);
    return (textured ? load : this.track(`scatter: ${assetId}`, pointsAnchor(group), load))
      .then(([baseTemplate, proxyTemplate]) => {
        if (textured && baseTemplate === null) return; // no textured base: the proxies stay
        const template = baseTemplate ?? proxyTemplate;
        if (template === null || gen !== this.generation || this.worldRoot !== root) return;
        // Both boxes are always known here, so `slotTransform` seats the asset's BASE on
        // the ground under each rock (a proxy-only template seats against its own box).
        const texBox = boxDimsOf(template);
        const refBox = proxyTemplate ? boxDimsOf(proxyTemplate) : texBox;
        const slots = group.map((prop) => ({
          x: prop.x,
          // The ground, not the placeholder's centre (q-o071-playtest-fixes (c)).
          y: propGroundY(prop),
          z: prop.z,
          scale: prop.rock.scale,
          rotationY: prop.rock.rotationY,
        }));
        const upgraded = placeScatterTemplate(template, texBox, refBox, slots);
        root.add(upgraded);
        // Refused by instancing (behavioural template): the static merge still applies,
        // except to a held proxy group, which the textured one must replace whole.
        if (hold) {
          const at = centroidOf(group);
          this.upgrades.add(at.x, at.z, () =>
            this.upgradeScatter(root, upgraded, assetId, group, loader, gen, true),
          );
        } else this.enqueueMerge(upgraded, root, gen);
        this.retargetCullable(placeholder, upgraded);
        root.remove(placeholder);
        // The rock placeholder owns its geometry; a proxy shares its cached template's.
        if (!textured) disposeObject(placeholder);
      })
      .catch((error: unknown) => this.onWarn(`scatter upgrade '${assetId}' failed`, error));
  }

  /** Point a cullable at its replacement object after an upgrade swap (centroid unchanged). */
  private retargetCullable(from: THREE.Object3D, to: THREE.Object3D): void {
    for (const cullable of this.cullables) {
      if (cullable.object === from) cullable.object = to;
    }
  }

  /**
   * Per-frame distance culling (WR.3.4), called from the Game render loop with the
   * camera position. Scatter groups beyond the LOD ladder's cull radius are hidden;
   * frustum culling (off-screen) stays Three.js-automatic. Markers are landmarks —
   * never distance-culled. The `selectLod` index is the seam a future per-LOD
   * mesh-swap hangs off (the placeholder + slice carry a single LOD today).
   */
  updateView(cameraPos: THREE.Vector3): void {
    this.lastEye.x = cameraPos.x;
    this.lastEye.z = cameraPos.z;
    this.mergeQueue.step(MERGE_SLICE_MS);
    // AD-840: the cloud sea drifts on the sky's clock (still under `skystill`).
    this.island?.animate(this.skyStill ? 0 : performance.now() / 1000);
    if (this.groundCover) {
      // The wind clock follows the sky's: held at 0 under `skystill` so lane frames repeat.
      this.groundCover.uniforms.uGcTime.value = this.skyStill ? 0 : performance.now() / 1000;
      for (const { chunk, mesh } of this.groundCover.chunks) {
        mesh.visible = chunkVisible(chunk, cameraPos.x, cameraPos.z, this.tier);
      }
    }
    if (this.cullables.length === 0) return;
    for (const cullable of this.cullables) {
      const dist = Math.hypot(cullable.x - cameraPos.x, cullable.z - cameraPos.z);
      cullable.object.visible = selectLod(dist) !== LOD_CULLED;
    }
  }

  private buildMarkers(
    root: THREE.Group,
    markers: MarkerPlan[],
    loader: AssetLoader | null,
    gen: number,
  ): void {
    const group = new THREE.Group();
    group.name = 'markers';
    group.visible = this.markersVisible;
    root.add(group);
    this.markersGroup = group;
    for (const marker of markers) {
      const mesh = new THREE.Mesh(markerGeometry(marker.kind), markerMaterial(marker.kind));
      mesh.position.set(marker.x, marker.y, marker.z);
      mesh.castShadow = true; // VQ-001 (upgraded pack assets get flags via the loader)
      group.add(mesh);
      this.upgrade(
        root,
        mesh,
        marker.upgradeAssetId,
        loader,
        gen,
        marker.x,
        marker.y,
        marker.z,
        group,
      );
    }
  }

  private buildColliderOverlay(root: THREE.Group, shapes: ColliderPlan[]): void {
    if (shapes.length === 0) return;
    const material = new THREE.MeshStandardMaterial({
      color: '#5b6b86',
      transparent: true,
      opacity: 0.5,
      roughness: 0.9,
    });
    for (const shape of shapes) {
      let mesh: THREE.Mesh;
      if (shape.kind === 'circle') {
        mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(shape.radius, shape.radius, 2, 20),
          material,
        );
        mesh.position.set(shape.x, 1 + (shape.groundY ?? 0), shape.z);
      } else if (shape.kind === 'aabb') {
        mesh = new THREE.Mesh(new THREE.BoxGeometry(shape.width, 2, shape.depth), material);
        mesh.position.set(shape.cx, 1 + (shape.groundY ?? 0), shape.cz);
      } else {
        mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(shape.radius, shape.radius, 2, 18),
          material,
        );
        mesh.position.set(shape.cx, 1 + (shape.groundY ?? 0), shape.cz);
      }
      root.add(mesh);
    }
  }

  /**
   * Kick an async upgrade: instantiate `assetId` and, if it still belongs to the
   * current world (`gen`), swap it in for the `placeholder` at (x,y,z). A null
   * result (unbound/unknown/no-url/load-failure) silently keeps the placeholder.
   */
  private upgrade(
    root: THREE.Group,
    placeholder: THREE.Object3D,
    assetId: string | null,
    loader: AssetLoader | null,
    gen: number,
    x: number,
    y: number,
    z: number,
    parent: THREE.Object3D = root,
  ): void {
    if (!assetId || !loader) return;
    const fullDetail = fullDetailAssetsActive();
    void this.track(
      `marker: ${assetId}`,
      pointsAnchor([{ x, y, z }]),
      Promise.all([
        loader.instantiatePreferred(assetId, fullDetail),
        fullDetail ? loader.template(assetId, 0) : Promise.resolve(null),
      ]),
    )
      .then(([object, proxyTemplate]) => {
        if (object === null || gen !== this.generation || this.worldRoot !== root) return;
        if (fullDetail && proxyTemplate) {
          const texBox = boxDimsOf(object);
          const refBox = boxDimsOf(proxyTemplate);
          const seat = seatTexturedPlacement(texBox, refBox, { x, y, z, scale: 1 });
          object.scale.set(seat.scaleX, seat.scaleY, seat.scaleZ);
          object.position.set(seat.x, seat.y, seat.z);
        } else {
          object.position.set(x, y, z);
        }
        // The replacement lands where the placeholder lived (the markers group for
        // a marker), so a hidden group stays hidden after the upgrade resolves.
        parent.add(object);
        parent.remove(placeholder);
        disposeObject(placeholder);
      })
      .catch((error: unknown) => this.onWarn(`upgrade '${assetId}' failed`, error));
  }

  /** Remove and free the sky dome, if any. */
  private removeSky(): void {
    if (this.skyMesh) {
      this.scene.remove(this.skyMesh);
      disposeObject(this.skyMesh);
    }
    this.skyMesh = null;
    this.skyKey = '';
  }

  /** Drop the whole world (e.g. on Game.dispose()). */
  clear(): void {
    this.removeSky();
    for (const light of this.lights) dropLight(this.scene, light);
    this.lights = [];
    if (this.worldRoot) {
      this.scene.remove(this.worldRoot);
      disposeObject(this.worldRoot);
      this.worldRoot = null;
    }
    if (this.loader) this.loader.clear();
    this.loader = null;
    this.mergeQueue.clear();
    this.cullables = [];
    this.markersGroup = null;
    this.terrainMesh = null;
    this.groundCover = null;
    this.island = null;
    this.seaExtent = null;
  }

  /** E3.3.S5 ground-cover stats (chunks · clumps · chunks currently drawn) for the probe/dev pages. */
  groundCoverStats(): { chunks: number; clumps: number; visibleChunks: number } {
    const cover = this.groundCover;
    if (!cover) return { chunks: 0, clumps: 0, visibleChunks: 0 };
    let clumps = 0;
    let visibleChunks = 0;
    for (const { chunk, mesh } of cover.chunks) {
      clumps += chunk.clumps.length;
      if (mesh.visible) visibleChunks += 1;
    }
    return { chunks: cover.chunks.length, clumps, visibleChunks };
  }

  /**
   * E3.3.S5 (AD-159): instanced grass on a ground whose palette grows it — one
   * InstancedMesh per chunk, distance-culled per tier in `updateView`. Never merged
   * (it is not under a dressing group) and never a collider or raycast target.
   */
  private buildGroundCoverLayer(root: THREE.Group, plan: ScenePlan): void {
    if (this.groundCoverFactory === null) return;
    const tier: RenderTier =
      this.tier === 'mobile' || activeQualityProfile().low ? 'mobile' : this.tier;
    const cover = planGroundCover(coverInputFromPlan(plan), tier);
    if (cover === null) return;
    const sun = plan.lights.find((l) => l.kind === 'directional');
    const handle = buildGroundCover(
      cover,
      {
        groundColor: resolveGroundPalette(plan.ground.palette ?? null).base,
        sun: sun && sun.kind === 'directional' ? sun : { x: 0.4, y: 1, z: 0.3 },
      },
      this.groundCoverFactory,
    );
    root.add(handle.group);
    this.groundCover = handle;
  }
}

/**
 * One `InstancedMesh` for a placeholder scatter group: every rock shares the
 * dodecahedron geometry + material (1 draw call), each instance carrying its own
 * position/scale/rotation. This is the WR.3.4 dense-scatter perf increment.
 */
/**
 * One `InstancedMesh` of placeholder boxes for undeliverable dressing pieces
 * (E3.4.S2). Sized to read as a small structure rather than a prop, and given a
 * flat unlit-ish grey so it is obviously scaffolding in a screenshot.
 */
function makeDressingPlaceholders(group: readonly DressingPlan[]): THREE.InstancedMesh {
  const geometry = new THREE.BoxGeometry(2, 3, 2);
  // Base at y=0 (flat-sim invariant, AD-044) — Box geometry is centred by default.
  geometry.translate(0, 1.5, 0);
  const material = new THREE.MeshStandardMaterial({ color: '#6b7280', roughness: 1 });
  const mesh = new THREE.InstancedMesh(geometry, material, group.length);
  const matrix = new THREE.Matrix4();
  const quaternion = new THREE.Quaternion();
  const yAxis = new THREE.Vector3(0, 1, 0);
  const position = new THREE.Vector3();
  const scale = new THREE.Vector3();
  group.forEach((placement, i) => {
    quaternion.setFromAxisAngle(yAxis, placement.rotationY);
    position.set(placement.x, placement.y, placement.z);
    scale.setScalar(placement.scale);
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(i, matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * AD-408: the legacy grid draped on the heightfield — one `LineSegments` draw with
 * GridHelper's two colours (centre lines vs the rest) and the grid's opacity.
 */
function makeDrapedGrid(
  heightfield: NonNullable<GroundPlan['heightfield']>,
  ground: GroundPlan,
): THREE.LineSegments {
  const grid = ground.grid as NonNullable<GroundPlan['grid']>;
  const arrays = drapedGridArrays(heightfield, {
    x: ground.x,
    z: ground.z,
    size: grid.size,
    divisions: grid.divisions,
    lift: grid.y,
  });
  const centre = new THREE.Color(grid.colorCenter);
  const line = new THREE.Color(grid.colorGrid);
  const colors = new Float32Array(arrays.centre.length * 3);
  arrays.centre.forEach((flag, i) => {
    const c = flag ? centre : line;
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(arrays.positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const material = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: grid.opacity * GROUND_GRID_SOFTEN,
    toneMapped: false,
  });
  const segments = new THREE.LineSegments(geometry, material);
  segments.name = 'terrain-grid';
  return segments;
}

function makeRockInstances(props: PropPlan[]): THREE.InstancedMesh {
  const geometry = new THREE.DodecahedronGeometry(1);
  const material = new THREE.MeshStandardMaterial({ color: '#3a4a66', roughness: 0.9 });
  const mesh = new THREE.InstancedMesh(geometry, material, props.length);
  const matrix = new THREE.Matrix4();
  const quaternion = new THREE.Quaternion();
  const yAxis = new THREE.Vector3(0, 1, 0);
  const position = new THREE.Vector3();
  const scale = new THREE.Vector3();
  props.forEach((prop, i) => {
    quaternion.setFromAxisAngle(yAxis, prop.rock.rotationY);
    position.set(prop.x, prop.y, prop.z);
    scale.setScalar(prop.rock.scale);
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(i, matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.castShadow = true; // VQ-001: scatter rocks ground themselves with shadows
  mesh.receiveShadow = true;
  return mesh;
}

function makeLight(spec: LightPlan, shadows: ShadowParams): THREE.Object3D {
  if (spec.kind === 'hemisphere') {
    return new THREE.HemisphereLight(spec.sky, spec.ground, spec.intensity);
  }
  if (spec.kind === 'ambient') {
    // VQ-001 (AD-194): the low ambient floor of the calibrated rig.
    return new THREE.AmbientLight(spec.color, spec.intensity);
  }
  const sun = new THREE.DirectionalLight(spec.color, spec.intensity);
  sun.position.set(spec.x, spec.y, spec.z);
  // VQ-001 (AD-194): the sun is the ONLY shadow caster; the tier profile decides
  // whether shadows draw and at what resolution. The ortho shadow camera covers
  // the 80×80 placeholder world with margin; biases tuned against acne/peter-
  // panning on the flat ground plane.
  if (shadows.enabled && shadows.mapSize > 0) {
    sun.castShadow = true;
    sun.shadow.mapSize.set(shadows.mapSize, shadows.mapSize);
    sun.shadow.camera.left = -55;
    sun.shadow.camera.right = 55;
    sun.shadow.camera.top = 55;
    sun.shadow.camera.bottom = -55;
    sun.shadow.camera.near = 1;
    // AD-9005: a low sun sits far from the origin along a shallow ray, so the ortho box must
    // reach ~70 m PAST the origin to cover the ground on the far side (the 54° rig fitted 120).
    sun.shadow.camera.far = Math.max(120, Math.hypot(spec.x, spec.y, spec.z) + 70);
    sun.shadow.bias = -0.0005;
    sun.shadow.normalBias = 0.02;
  }
  return sun;
}

function markerGeometry(kind: MarkerPlan['kind']): THREE.BufferGeometry {
  if (kind === 'shrine') return new THREE.ConeGeometry(0.5, 2.2, 6);
  if (kind === 'transition') return new THREE.TorusGeometry(1.1, 0.16, 8, 24);
  return new THREE.CylinderGeometry(0.22, 0.22, 1.6, 8); // poi
}

function markerMaterial(kind: MarkerPlan['kind']): THREE.MeshStandardMaterial {
  if (kind === 'shrine') {
    return new THREE.MeshStandardMaterial({
      color: '#36d6e0',
      emissive: '#0e4d52',
      roughness: 0.4,
    });
  }
  if (kind === 'transition') {
    return new THREE.MeshStandardMaterial({
      color: '#d36bff',
      emissive: '#3a1147',
      roughness: 0.4,
    });
  }
  return new THREE.MeshStandardMaterial({ color: '#e6b450', emissive: '#3a2a08', roughness: 0.6 }); // poi
}

/**
 * Free a removed subtree's OWN GPU resources. Asset placements are clones of the
 * loader's templates; `disposeObject3D` leaves those shared buffers to
 * `loader.clear()` (assets/gpuOwnership.ts, H-1349) — this used to free them here.
 */
function disposeObject(object: THREE.Object3D): void {
  disposeObject3D(object);
}

/** Remove a light and free what it owns (a shadow-casting light's shadow map). */
function dropLight(scene: THREE.Scene, light: THREE.Object3D): void {
  scene.remove(light);
  (light as { dispose?: () => void }).dispose?.();
}

/** The (x, z) centroid of a placement group (where its upgrade sits in the queue). */
function centroidOf(group: readonly { x: number; z: number }[]): { x: number; z: number } {
  let x = 0;
  let z = 0;
  for (const p of group) {
    x += p.x;
    z += p.z;
  }
  const n = group.length || 1;
  return { x: x / n, z: z / n };
}
